import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, RefreshCw, Copy, CheckCircle } from 'react-feather';
import styles from './ErrorBoundary.module.css';

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 *
 * Features:
 * - User-friendly error message
 * - Retry button to reset error state
 * - Copy error details to clipboard
 * - Dark mode support
 * - Mobile-first responsive design
 * - Logs errors to console and NewRelic (if available)
 *
 * @example
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Log to NewRelic if available
    if (this.props.newrelic) {
      this.props.newrelic.noticeError(error, {
        errorInfo: errorInfo.componentStack,
        errorId: this.state.errorId,
      });
    }

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
    });
  };

  handleCopyError = async () => {
    const { error, errorInfo, errorId } = this.state;

    const errorReport = `
=== ERROR REPORT ===
Error ID: ${errorId}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}

=== ERROR DETAILS ===
Type: ${error?.name || 'Error'}
Message: ${error?.message || error?.toString() || 'Unknown error'}

=== STACK TRACE ===
${error?.stack || 'No stack trace available'}

=== COMPONENT STACK ===
${errorInfo?.componentStack || 'No component stack available'}

=== BROWSER INFO ===
User Agent: ${navigator.userAgent}
Language: ${navigator.language}
Platform: ${navigator.platform}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorReport);
      this.setState({ copied: true });

      // Reset copied state after 2 seconds
      setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  render() {
    const { hasError, error, errorId, copied } = this.state;
    const { children, fallbackTitle, fallbackMessage } = this.props;

    if (hasError) {
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorContainer}>
            {/* Error Icon */}
            <div className={styles.errorIcon}>
              <AlertTriangle size={64} />
            </div>

            {/* Error Title */}
            <h1 className={styles.errorTitle}>
              {fallbackTitle || 'Something went wrong'}
            </h1>

            {/* Error Message */}
            <p className={styles.errorMessage}>
              {fallbackMessage ||
                'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
            </p>

            {/* Error ID Badge */}
            {errorId && (
              <div className={styles.errorId}>
                <code>Error ID: {errorId}</code>
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.actions}>
              <button
                className={styles.retryButton}
                onClick={this.handleRetry}
                aria-label="Retry"
              >
                <RefreshCw size={18} />
                <span>Try Again</span>
              </button>

              <button
                className={`${styles.copyButton} ${copied ? styles.copied : ''}`}
                onClick={this.handleCopyError}
                aria-label="Copy error details"
              >
                {copied ? (
                  <>
                    <CheckCircle size={18} />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    <span>Copy Details</span>
                  </>
                )}
              </button>
            </div>

            {/* Error Details (Collapsible) */}
            {error && (
              <details className={styles.errorDetails}>
                <summary className={styles.errorDetailsSummary}>
                  Technical Details
                </summary>
                <div className={styles.errorDetailsContent}>
                  <pre className={styles.errorPre}>
                    <strong>{error.name}:</strong> {error.message}
                    {'\n\n'}
                    {error.stack}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackTitle: PropTypes.string,
  fallbackMessage: PropTypes.string,
  onError: PropTypes.func,
  newrelic: PropTypes.object,
};

ErrorBoundary.defaultProps = {
  fallbackTitle: null,
  fallbackMessage: null,
  onError: null,
  newrelic: null,
};

export default ErrorBoundary;
